/**
 * auth.js
 * Supabase authentication module for ZAGX v2
 * Extracted from index.html v0.7.1
 *
 * Exports:
 *   - SUPABASE_URL, SUPABASE_ANON_KEY (config)
 *   - _sb, _sbUser, _sbReady, _accessToken, _isOnline, _savePending, _saveTimer (shared state)
 *   - SB_REST (REST endpoint)
 *   - sbInit(), authLogin(), authLogout(), authCheckSession()
 *   - showAuth(), hideAuth(), updateSyncBadge()
 *   - ensureValidToken(), _sbHeaders()
 *   - (cloudSave/cloudLoad/cloudSnapshot → moved to persistence.js)
 *   - _setOnLoginSuccess(), _setOnOnline() (callbacks)
 */

import { createClient } from '@supabase/supabase-js';
import { esc } from './utils.js';

// --- CONFIGURATION: Set via VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env ---
// STABILIZATION: No hardcoded fallback — fail loudly if env vars missing
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('[ZAGX] CRITICAL: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set. Cloud sync will fail.');
}

// --- SHARED STATE ---
export let _sb = null; // Supabase client instance
export let _sbUser = null; // Current authenticated user
export let _sbReady = false; // True when Supabase auth + initial load succeeds
export let _accessToken = null; // Cached JWT access token
export let _isOnline = navigator.onLine;
export let _savePending = false; // Debounce flag for cloud saves
export let _saveTimer = null;
let _tokenRefreshInterval = null; // PERF-03: track token refresh interval for cleanup

// --- SUPABASE REST ENDPOINT ---
export const SB_REST = SUPABASE_URL + '/rest/v1';

// --- CALLBACKS (to avoid circular imports) ---
let _onLoginSuccess = null;
let _onOnline = null;

export function _setOnLoginSuccess(fn) {
  _onLoginSuccess = fn;
}

export function _setOnOnline(fn) {
  _onOnline = fn;
}

// --- INIT SUPABASE CLIENT ---
export function sbInit() {
  _sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  // Listen for ALL auth state changes — capture token on every event
  _sb.auth.onAuthStateChange((event, session) => {
    console.log('[SB] Auth event:', event, session ? 'has session' : 'no session');
    if (session && session.access_token) {
      _accessToken = session.access_token;
      _sbUser = session.user;
      _sbReady = true;
    }
    if (event === 'SIGNED_OUT') {
      _sbUser = null;
      _sbReady = false;
      _accessToken = null;
      showAuth();
    }
  });

  // Online/offline listeners
  window.addEventListener('online', () => {
    _isOnline = true;
    updateSyncBadge('cloud', 'synced');
    if (_onOnline) _onOnline();
  });

  window.addEventListener('offline', () => {
    _isOnline = false;
    updateSyncBadge('offline', 'offline');
  });

  // Periodic token refresh check every 5 minutes — PERF-03: store interval ID for cleanup
  _tokenRefreshInterval = setInterval(() => {
    if (_sbReady && _isOnline) ensureValidToken();
  }, 300000);
}

// --- AUTH FUNCTIONS ---
export async function authLogin() {
  const email = document.getElementById('authEmail').value.trim();
  const pass = document.getElementById('authPass').value;
  const errEl = document.getElementById('authErr');
  const statusEl = document.getElementById('authStatus');
  const btn = document.getElementById('authBtn');

  errEl.textContent = '';
  if (!email || !pass) {
    errEl.textContent = 'Email and password required';
    return;
  }

  btn.disabled = true;
  statusEl.textContent = 'Signing in...';

  try {
    // Try sign in first
    let { data, error } = await _sb.auth.signInWithPassword({ email, password: pass });

    if (error && error.message.includes('Invalid login')) {
      // If not registered, auto-register (first time setup)
      statusEl.textContent = 'Creating account...';
      const reg = await _sb.auth.signUp({ email, password: pass });
      if (reg.error) {
        errEl.textContent = reg.error.message;
        btn.disabled = false;
        statusEl.textContent = '';
        return;
      }
      // Sign in after registration
      const login2 = await _sb.auth.signInWithPassword({ email, password: pass });
      if (login2.error) {
        errEl.textContent = 'Registered but login failed. Check email confirmation settings.';
        btn.disabled = false;
        return;
      }
      data = login2.data;
    } else if (error) {
      errEl.textContent = error.message;
      btn.disabled = false;
      statusEl.textContent = '';
      return;
    }

    _sbUser = data.user;
    _accessToken = data.session ? data.session.access_token : null;
    _sbReady = true;
    console.log('[SB] Login OK, token:', _accessToken ? 'captured' : 'MISSING');

    hideAuth();
    statusEl.textContent = '';
    btn.disabled = false;

    // Call login success callback
    if (_onLoginSuccess) {
      await _onLoginSuccess();
    }
  } catch(e) {
    errEl.textContent = 'Connection error: ' + e.message;
    btn.disabled = false;
  }
}

export async function authLogout() {
  if (_tokenRefreshInterval) { clearInterval(_tokenRefreshInterval); _tokenRefreshInterval = null; } // PERF-03
  if (_sb) await _sb.auth.signOut();
  _sbUser = null;
  _sbReady = false;
  showAuth();
}

export async function authCheckSession() {
  if (!_sb) return false;
  try {
    const { data: { session } } = await _sb.auth.getSession();
    if (session && session.user) {
      _sbUser = session.user;
      _accessToken = session.access_token || null;
      _sbReady = true;
      console.log('[SB] Session restored, token:', _accessToken ? 'OK' : 'MISSING');
      return true;
    }
  } catch(e) {
    console.warn('[SB] Session check failed:', e);
  }
  return false;
}

export function showAuth() {
  document.getElementById('authOverlay').classList.remove('hidden');
}

export function hideAuth() {
  document.getElementById('authOverlay').classList.add('hidden');
}

// --- SYNC BADGE ---
export function updateSyncBadge(dot, label) {
  const el = document.getElementById('syncBadge');
  if (!el) return;
  const dotClass = dot === 'cloud' ? 'dot-cloud' : dot === 'offline' ? 'dot-offline' : 'dot-error';
  // D1: a sync failure must be LOUD — a stale cloud silently drifting behind local is
  // exactly what hid the empty-tax-summary incident. Make error a red pulsing pill,
  // offline an amber note, and spell out the consequence in the tooltip.
  el.classList.toggle('sync-alert', dot === 'error');
  el.classList.toggle('sync-warn', dot === 'offline');
  el.title = dot === 'error'
    ? 'Sync FAILED — changes are saved on this device but NOT in the cloud. Check connection, then reload.'
    : dot === 'offline' ? 'Offline — showing local data, not synced to the cloud' : 'Sync status';
  // CE-10 Fix: Escape label to prevent XSS via dynamic sync status
  el.innerHTML = '<span class="dot-sync ' + dotClass + '"></span> ' + esc(label);
}

// --- TOKEN MANAGEMENT ---
// Proactive JWT refresh — checks expiry and refreshes 2 min before
export async function ensureValidToken() {
  if (!_sb || !_accessToken) return;

  try {
    // CE-09 Fix: Defensive JWT decode with validation guards
    const parts = _accessToken.split('.');
    if (!parts || parts.length < 2) {
      console.warn('[SB] Malformed token — missing segments');
      return;
    }

    let payload;
    try {
      payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch (decodeErr) {
      console.warn('[SB] Token decode failed:', decodeErr.message);
      return;
    }

    if (!payload || typeof payload.exp !== 'number') {
      console.warn('[SB] Token missing exp claim');
      return;
    }

    const exp = payload.exp * 1000; // ms
    const now = Date.now();

    if (exp - now < 120000) { // less than 2 min to expiry
      console.log('[SB] Token expires in', Math.round((exp - now) / 1000) + 's, refreshing...');
      const { data, error } = await _sb.auth.refreshSession();
      if (data?.session) {
        _accessToken = data.session.access_token;
        _sbUser = data.session.user;
        console.log('[SB] Token refreshed OK');
      } else {
        console.warn('[SB] Token refresh failed:', error?.message);
      }
    }
  } catch (e) {
    console.warn('[SB] Token check error:', e);
  }
}

export async function _sbHeaders() {
  // Proactively refresh token if near expiry
  await ensureValidToken();

  // Try cached token first, then SDK session as fallback
  let token = _accessToken;
  if (!token) {
    try {
      const { data: { session } } = await _sb.auth.getSession();
      if (session && session.access_token) {
        token = session.access_token;
        _accessToken = token;
      }
    } catch (_e) {}
  }

  if (!token) {
    console.warn('[SB] No access token available');
    return null;
  }

  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': 'Bearer ' + token,
    'Content-Type': 'application/json',
    'Prefer': 'return=minimal'
  };
}

// --- CLOUD OPERATIONS ---
// STABILIZATION: All cloud operations (cloudSave, cloudLoad, cloudSnapshot)
// are in persistence.js. These stubs are removed to prevent accidental import.
// If you see "cloudSave is not exported from auth.js" — that's intentional,
// import from persistence.js instead.
